import { z } from "zod";
import {
    DocumentMigrationErrorCode,
    DocumentMigrationIssueSeverity,
} from "../constants/accounting-migration";
import { AccountClass } from "../constants/domains/accounting/accounts";
import {
    AccountingMigrationExternalSystem,
    MigrationObjectType,
} from "../types/domains/accounting/accountingMigration";
import {
    areBillPrerequisitesMet,
    areInvoicePrerequisitesMet,
} from "../helpers/accountingMigrationObjectTypes";
import { importRejectionSchema } from "./domains/accounting/import-rejection.schema";
import { importedAccountSchema } from "./domains/accounting/importedAccount.schema";
import { importedAccountGroupSchema } from "./domains/accounting/importedAccountGroup.schema";

export const migrationObjectTypeSchema = z.nativeEnum(MigrationObjectType);

export const refreshAccountingMigrationBodySchema = z.object({
    objectTypes: z.array(migrationObjectTypeSchema).min(1).optional(),
});

export const accountingMigrationSchema = z.object({
    id: z.string().uuid(),
    entityId: z.number(),
    integrationConnectionId: z.string().uuid().nullable(),
    externalSystem: z.nativeEnum(AccountingMigrationExternalSystem).nullable(),
    syncCounterparties: z.boolean(),
    syncGeneralLedger: z.boolean(),
    syncChartOfAccounts: z.boolean(),
    syncAttachments: z.boolean().nullable(),
    syncClasses: z.boolean().nullable(),
    syncCatalogItems: z.boolean().nullable(),
    syncInvoices: z.boolean().nullable(),
    syncBills: z.boolean().nullable(),
    accountsMappedAt: z.coerce.date().nullable(),
    accountsMappingFinalizedAt: z.coerce.date().nullable(),
    journalEntriesSyncedAt: z.coerce.date().nullable(),
    manualJournalEntriesConvertedAt: z.coerce.date().nullable(),
    refreshRequestedAt: z.coerce.date().nullable(),
    startDate: z.string().date().nullable(),
    openingBalanceDate: z.string().date().nullable(),
    balanceSheetFinancialDocumentId: z.coerce.number().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    isChartImportedAsIs: z.boolean().optional(),
});

export const createAccountingMigrationBodySchema = z
    .object({
        startDate: z.string().date().optional(),
        integrationConnectionId: z.string().uuid().optional(),
        externalSystem: z
            .nativeEnum(AccountingMigrationExternalSystem)
            .nullable()
            .optional(),
        syncChartOfAccounts: z.boolean().optional().default(false),
        shouldUseExternalChartOfAccounts: z.boolean().optional().default(false),
        syncGeneralLedger: z.boolean().optional().default(false),
        syncCounterparties: z.boolean().optional().default(false),
        syncAttachments: z.boolean().optional().default(false),
        syncClasses: z.boolean().optional().default(false),
        syncCatalogItems: z.boolean().optional().default(false),
        syncInvoices: z.boolean().optional().default(false),
        syncBills: z.boolean().optional().default(false),
    })
    .refine(
        (body) =>
            areInvoicePrerequisitesMet({
                syncInvoices: body.syncInvoices,
                syncCounterparties: body.syncCounterparties,
                syncCatalogItems: body.syncCatalogItems,
                syncGeneralLedger: body.syncGeneralLedger,
                syncChartOfAccounts: body.syncChartOfAccounts,
                isConnectionMigration: Boolean(body.integrationConnectionId),
            }),
        {
            message:
                "syncInvoices requires syncCounterparties, syncCatalogItems, syncGeneralLedger and, for connection migrations, syncChartOfAccounts",
            path: ["syncInvoices"],
        },
    )
    .refine(
        (body) =>
            areBillPrerequisitesMet({
                syncBills: body.syncBills,
                syncCounterparties: body.syncCounterparties,
                syncGeneralLedger: body.syncGeneralLedger,
                syncChartOfAccounts: body.syncChartOfAccounts,
                isConnectionMigration: Boolean(body.integrationConnectionId),
            }),
        {
            message:
                "syncBills requires syncCounterparties, syncGeneralLedger and, for connection migrations, syncChartOfAccounts",
            path: ["syncBills"],
        },
    );

export type AccountingMigrationResponse = z.infer<
    typeof accountingMigrationSchema
>;

export const updateMigrationStartDateBodySchema = z.object({
    startDate: z.string().date().nullable(),
});

export const updateMigrationEndDateBodySchema = z.object({
    endDate: z.string().date(),
});

export const createImportedAccountsFromBalanceSheetBodySchema = z.object({
    financialDocumentId: z.coerce.number(),
    accounts: z
        .array(
            z.object({
                name: z.string().min(1),
                classification: z.nativeEnum(AccountClass),
                balance: z.number().nullable(),
            }),
        )
        .min(1)
        .max(1000),
});

export type CreateImportedAccountsFromBalanceSheetBody = z.infer<
    typeof createImportedAccountsFromBalanceSheetBodySchema
>;

export const createImportedAccountsFromCoaBodySchema = z.object({
    accounts: z
        .array(
            z.object({
                name: z.string().min(1),
                classification: z.nativeEnum(AccountClass),
            }),
        )
        .min(1)
        .max(1000),
});

export type CreateImportedAccountsFromCoaBody = z.infer<
    typeof createImportedAccountsFromCoaBodySchema
>;

export const getAccountsResponseSchema = z.object({
    accounts: z.array(importedAccountSchema),
    accountGroups: z.array(importedAccountGroupSchema),
});

export const finalizeAccountsMappingBodySchema = z.object({
    isRefinalization: z.boolean().optional(),
});

// Sources whose migration data arrives as spreadsheet exports parsed by Kick.
export const coaDocumentMigrationSourceSchema = z.enum(["xero", "qbdt", "qbo"]);

export type CoADocumentMigrationSource = z.infer<
    typeof coaDocumentMigrationSourceSchema
>;

// All document-migration sources, including the single-JSON-file upload
// produced by the Kick QuickBooks Desktop extractor.
export const documentMigrationSourceSchema = z.enum([
    "xero",
    "qbdt",
    "qbdt_json",
    "qbo",
]);

export type DocumentMigrationSource = z.infer<
    typeof documentMigrationSourceSchema
>;

export const qbdJsonDocumentMigrationBodySchema = z.object({
    documentId: z.coerce.number().int().positive(),
    confirmWarnings: z.boolean().optional(),
});

export const qbdFileMigrationUploadSourceSchema = z.enum(["qbdt", "qbdt_json"]);

export type QbdFileMigrationUploadSource = z.infer<
    typeof qbdFileMigrationUploadSourceSchema
>;

export const setQbdFileMigrationUploadSourceBodySchema = z.object({
    source: qbdFileMigrationUploadSourceSchema,
});

// The basis a user can choose for a GL. Label-less exports parse as `unknown`
// but the user can only ever resolve them to cash or accrual.
export const glBasisSchema = z.enum(["cashGl", "accrualGl"]);

export type GlBasis = z.infer<typeof glBasisSchema>;

// The basis a GL parses as. `unknown` means the layout stated no basis, so the
// user must supply one before the GL can be migrated.
export const parsedGlBasisSchema = z.enum(["cashGl", "accrualGl", "unknown"]);

export type ParsedGlBasis = z.infer<typeof parsedGlBasisSchema>;

export const documentMigrationUploadBodySchema = z.object({
    coaDocumentId: z.coerce.number(),
    glDocumentIds: z.array(z.coerce.number()).max(100).optional(),
});

export const glIssuePeriodSchema = z.object({
    start: z.string().date().nullable(),
    end: z.string().date().nullable(),
});

export const glIssueSchema = z.object({
    type: z.enum(["overlap", "gap"]),
    severity: z.enum(["blocking", "warning"]),
    message: z.string(),
    periods: z.array(glIssuePeriodSchema).optional(),
});

export type GlIssueDto = z.infer<typeof glIssueSchema>;

// The QBD JSON import runs in the background. `importStarted` is false when
// the file needs a warning confirmation before the job is enqueued.
export const qbdJsonDocumentMigrationResponseSchema = z.object({
    importStarted: z.boolean(),
    warnings: z.array(z.string()).optional(),
});

export const documentMigrationUploadResponseSchema = z.object({
    accountsCount: z.number(),
    cashJeCount: z.number(),
    accrualJeCount: z.number(),
    issues: z.array(glIssueSchema),
    warnings: z.array(z.string()).optional(),
});

export const unbalancedJournalEntryDaySchema = z.object({
    date: z.string(),
    basis: z.enum(["cash", "accruals"]),
    debits: z.number(),
    credits: z.number(),
});

export type UnbalancedJournalEntryDayDto = z.infer<
    typeof unbalancedJournalEntryDaySchema
>;

// A single accounting-logic problem the validate step found, attributed to the
// document that owns it (the CoA for code mismatches, the GL for unbalanced
// days) so the UI can flag that row. `documentId` is null when no single
// document owns the issue.
export const documentMigrationIssueSchema = z.object({
    code: z.nativeEnum(DocumentMigrationErrorCode),
    severity: z.nativeEnum(DocumentMigrationIssueSeverity),
    documentId: z.coerce.number().nullable(),
    missingCodes: z.array(z.string()).optional(),
    unbalancedDays: z.array(unbalancedJournalEntryDaySchema).optional(),
});

export type DocumentMigrationIssueDto = z.infer<
    typeof documentMigrationIssueSchema
>;

export const validateDocumentMigrationResponseSchema = z.object({
    issues: z.array(documentMigrationIssueSchema),
});

// `unknownGl` is the role of a GL whose basis the parser could not read; the
// user resolves it to cash or accrual before finalizing.
export const migrationFileTypeSchema = z.enum([
    "coa",
    "cashGl",
    "accrualGl",
    "unknownGl",
]);

export type MigrationFileType = z.infer<typeof migrationFileTypeSchema>;

export const documentMigrationUploadErrorSchema = z.object({
    code: z.nativeEnum(DocumentMigrationErrorCode),
    message: z.string(),
    file: migrationFileTypeSchema.optional(),
    missingCodes: z.array(z.string()).optional(),
    unbalancedDays: z.array(unbalancedJournalEntryDaySchema).optional(),
    // Documents whose unknown basis must be resolved before finalizing.
    missingBasisDocumentIds: z.array(z.coerce.number()).optional(),
    // Structured reason for an IMPORT_REJECTED error, citing the offending rows.
    rejection: importRejectionSchema.optional(),
});

export const setGlExtractionBasisBodySchema = z.object({
    documentId: z.coerce.number(),
    basis: glBasisSchema,
});

export type SetGlExtractionBasisBody = z.infer<
    typeof setGlExtractionBasisBodySchema
>;

export const extractMigrationFileBodySchema = z.object({
    documentId: z.coerce.number(),
});

export const extractMigrationFileResponseSchema = z.object({
    fileType: migrationFileTypeSchema,
    accountsCount: z.number().optional(),
    journalEntriesCount: z.number().optional(),
    entriesCount: z.number().optional(),
    period: glIssuePeriodSchema.nullable().optional(),
    basis: parsedGlBasisSchema.optional(),
    warnings: z.array(z.string().max(500)).max(50).optional(),
});
